import { basename } from "node:path";
import { createHash } from "node:crypto";
import { readFile } from "node:fs/promises";
import { spawn } from "node:child_process";
import { createInterface } from "node:readline/promises";
import { stdin as input, stdout as output } from "node:process";
import type { Command } from "commander";
import type { ResolvedConfig } from "../config.js";
import { CliApiClient, type CliSubmissionResponse } from "../api/cli-api.js";
import { CLI_VERSION } from "../constants.js";
import { AspError } from "../utils/errors.js";
import {
  printInfo,
  printJson,
  printSuccess,
  printTable,
  printWarn,
} from "../utils/output.js";
import { formatPlatformLabel, requireCredentials } from "../utils/platform.js";
import {
  appendOptionalArtifactFields,
  looksLikeSubmissionId,
} from "../utils/artifact-publish.js";
import { recordPluginPublish } from "../utils/publish-registry.js";
import { resolvePluginSubmissionId } from "../utils/submission-resolve.js";
import {
  pollUntilTerminal,
} from "../utils/submission-poll.js";
import { formatPluginListTechType } from "../utils/marketplace-display.js";

interface SearchOptions {
  category?: string;
  source?: "official" | "verified" | "community";
  sort?: "recommended" | "downloads" | "updated_at";
  pageSize?: number;
  cursor?: string;
}

interface InfoOptions {
  version?: string;
}

interface InstallOptions {
  version?: string;
  via?: "qwenpaw";
}

interface PublishOptions {
  url?: string;
  artifactId?: string;
  version?: string;
  category?: PluginCategory;
  repoUrl?: string;
  publishMode?: "auto_after_scan";
  noWait?: boolean;
}

interface PublishStatusOptions {
  watch?: boolean;
}

interface PublishLogsOptions {}

type PluginCategory = "tool" | "provider" | "command" | "hook" | "frontend" | "general";
type PluginManifest = { id?: string; version?: string; type?: string };

const PLUGIN_CATEGORY_OPTIONS: Array<{
  value: PluginCategory;
  label: string;
  describe: string;
}> = [
  {
    value: "tool",
    label: "Agent Tool",
    describe: "Functions, APIs, or capabilities that Agents can invoke",
  },
  {
    value: "provider",
    label: "Model Provider",
    describe: "Connect custom LLM providers or model endpoints",
  },
  {
    value: "command",
    label: "Slash Command",
    describe: "Register console commands in the form /xxx",
  },
  {
    value: "hook",
    label: "Lifecycle Hook",
    describe: "Run code at key moments such as startup and shutdown",
  },
  {
    value: "frontend",
    label: "UI Extension",
    describe: "Provide frontend JS bundles loaded dynamically by the UI",
  },
  {
    value: "general",
    label: "General Plugin",
    describe: "Does not fit the categories above, or combines multiple capabilities",
  },
];

async function runCommand(command: string, args: string[]): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const child = spawn(command, args, { stdio: ["ignore", "pipe", "pipe"] });
    let stdout = "";
    let stderr = "";
    child.stdout.on("data", (chunk) => {
      stdout += String(chunk);
    });
    child.stderr.on("data", (chunk) => {
      stderr += String(chunk);
    });
    child.on("error", reject);
    child.on("close", (code) => {
      if (code === 0) resolve(stdout);
      else reject(new Error(stderr || `Command failed: ${command} ${args.join(" ")}`));
    });
  });
}

async function readPluginManifestFromZip(zipPath: string | undefined): Promise<PluginManifest | undefined> {
  if (!zipPath) return undefined;
  try {
    const stdout = await runCommand("unzip", ["-p", zipPath, "plugin.json"]);
    return JSON.parse(stdout) as PluginManifest;
  } catch {
    return undefined;
  }
}

function getClient(getConfig: () => ResolvedConfig): CliApiClient {
  return new CliApiClient(getConfig());
}

async function runSearch(
  getConfig: () => ResolvedConfig,
  query: string | undefined,
  opts: SearchOptions,
): Promise<void> {
  const config = getConfig();
  const client = getClient(getConfig);
  const response = await client.searchPlugins({
    q: query,
    category: opts.category,
    source: opts.source,
    sort: opts.sort,
    page_size: opts.pageSize,
    cursor: opts.cursor,
  });

  if (config.json) {
    printJson(response);
    return;
  }

  if (response.items.length === 0) {
    printInfo("No plugins found.");
    return;
  }

  const rows = response.items.map((item) => [
    item.plugin_id,
    String(item.latest_version ?? "-"),
    formatPluginListTechType(item),
    String(item.name ?? "-"),
  ]);
  printTable(["plugin_id", "version", "tech_type", "name"], rows);
  if (response.next_cursor) {
    printInfo(`Next cursor: ${response.next_cursor}`);
  }
}

async function runInfo(
  getConfig: () => ResolvedConfig,
  pluginId: string,
  opts: InfoOptions,
): Promise<void> {
  const config = getConfig();
  const client = getClient(getConfig);
  const response = await client.getPluginInfo(pluginId, opts.version);
  if (config.json) {
    printJson(response);
    return;
  }

  printInfo(`Plugin: ${response.plugin_id}`);
  if (response.latest_version) printInfo(`Version: ${response.latest_version}`);
  if (response.scan_status) printInfo(`Scan: ${response.scan_status}`);
  if (response.install_command) printInfo(`Install: ${response.install_command}`);
  if (response.public_url) printInfo(`URL: ${response.public_url}`);
}

async function runVersions(getConfig: () => ResolvedConfig, pluginId: string): Promise<void> {
  const config = getConfig();
  const client = getClient(getConfig);
  const response = await client.getPluginVersions(pluginId);
  if (config.json) {
    printJson(response);
    return;
  }
  if (response.items.length === 0) {
    printInfo("No published versions.");
    return;
  }
  const rows = response.items.map((item) => [
    item.version,
    item.published_at ?? "-",
    item.deprecated ? "yes" : "no",
  ]);
  printTable(["version", "published_at", "deprecated"], rows);
}

async function runInstall(
  getConfig: () => ResolvedConfig,
  pluginId: string,
  opts: InstallOptions,
): Promise<void> {
  const base = getConfig();
  const { config } = await requireCredentials(base, base.platformUrl);
  const client = new CliApiClient(config);
  const intent = await client.createInstallIntent(pluginId, opts.version);

  if (base.json) {
    printJson(intent);
    return;
  }

  if (opts.via !== undefined && opts.via !== "qwenpaw") {
    throw new AspError("Only --via qwenpaw is supported now.", {
      code: "INVALID_REQUEST",
    });
  }

  const installCmd = `qwenpaw plugin install ${intent.download_url}`;
  printInfo(`Run to install:\n  ${installCmd}`);
  if (intent.sha512) printInfo(`sha512: ${intent.sha512}`);
  if (intent.expires_at) printInfo(`URL expires at: ${intent.expires_at}`);

  const child = spawn("qwenpaw", ["plugin", "install", intent.download_url], {
    stdio: "inherit",
  });
  await new Promise<void>((resolve, reject) => {
    child.on("exit", (code) => {
      if (code === 0) resolve();
      else reject(new AspError(`qwenpaw exited with code ${code ?? 1}`, { code: "QWENPAW_FAILED" }));
    });
    child.on("error", reject);
  });
}

async function uploadZipToSignedUrl(upload: {
  url: string;
  method?: string;
  headers?: Record<string, string>;
}, fileBuffer: Buffer): Promise<void> {
  const response = await fetch(upload.url, {
    method: upload.method ?? "PUT",
    headers: upload.headers ?? {},
    body: fileBuffer,
  });
  if (!response.ok) {
    throw new AspError(`Upload failed with HTTP ${response.status}`, {
      code: "UPLOAD_FAILED",
      statusCode: response.status,
    });
  }
}

function buildZipBody(
  filePath: string,
  fileBuffer: Buffer,
  sha512: string,
  opts: PublishOptions & { category: PluginCategory },
): Record<string, unknown> {
  return appendOptionalArtifactFields(
    {
      artifact_type: "plugin",
      package_source: "zip",
      category: opts.category,
      filename: basename(filePath),
      size_bytes: fileBuffer.byteLength,
      sha512,
      publish_mode: opts.publishMode ?? "auto_after_scan",
      client: {
        name: "agentscope-platform-cli",
        version: CLI_VERSION,
      },
    },
    {
      artifactId: opts.artifactId,
      version: opts.version,
      repoUrl: opts.repoUrl,
    },
  );
}

function buildUrlBody(opts: PublishOptions & { category: PluginCategory }): Record<string, unknown> {
  return appendOptionalArtifactFields(
    {
      artifact_type: "plugin",
      package_source: "url",
      source_url: opts.url,
      category: opts.category,
      publish_mode: opts.publishMode ?? "auto_after_scan",
      client: {
        name: "agentscope-platform-cli",
        version: CLI_VERSION,
      },
    },
    {
      artifactId: opts.artifactId,
      version: opts.version,
      repoUrl: opts.repoUrl,
    },
  );
}

function parseCategory(inputValue: string | undefined): PluginCategory | undefined {
  if (!inputValue) return undefined;
  const normalized = inputValue.trim().toLowerCase();
  if (PLUGIN_CATEGORY_OPTIONS.some((item) => item.value === normalized)) {
    return normalized as PluginCategory;
  }
  return undefined;
}

function tryParseCategoryFromUrl(urlValue: string | undefined): PluginCategory | undefined {
  if (!urlValue) return undefined;
  try {
    const parsed = new URL(urlValue);
    const queryCategory = parseCategory(parsed.searchParams.get("category") ?? undefined);
    if (queryCategory) return queryCategory;
    const segments = parsed.pathname
      .split("/")
      .map((item) => item.trim().toLowerCase())
      .filter(Boolean);
    for (let i = 0; i < segments.length; i += 1) {
      const current = parseCategory(segments[i]);
      if (current) return current;
    }
    return undefined;
  } catch {
    return undefined;
  }
}

function tryParsePluginIdFromUrl(urlValue: string | undefined): string | undefined {
  if (!urlValue) return undefined;
  try {
    const parsed = new URL(urlValue);
    const segments = parsed.pathname
      .split("/")
      .map((item) => item.trim())
      .filter(Boolean);
    const pluginsIdx = segments.findIndex((item) => item.toLowerCase() === "plugins");
    if (pluginsIdx >= 0 && segments[pluginsIdx + 2]) {
      return segments[pluginsIdx + 2];
    }
    const last = segments[segments.length - 1];
    if (last?.toLowerCase().endsWith(".zip")) {
      return last.replace(/-\d+\.\d+\.\d+.*\.zip$/i, "").replace(/\.zip$/i, "");
    }
    return undefined;
  } catch {
    return undefined;
  }
}

async function tryParseCategoryFromZip(
  zipPath: string | undefined,
  manifest?: PluginManifest,
): Promise<PluginCategory | undefined> {
  const parsed = manifest ?? (await readPluginManifestFromZip(zipPath));
  return parseCategory(parsed?.type);
}

function printManifestPreview(manifest: PluginManifest | undefined, jsonOutput: boolean): void {
  if (jsonOutput || !manifest) return;
  if (manifest.id) {
    printInfo(`Package plugin id: ${manifest.id} (parsed on complete if --artifact-id is omitted)`);
  }
  if (manifest.version) {
    printInfo(`Package version: ${manifest.version} (parsed on complete if --version is omitted)`);
  }
}

function validateExplicitManifestOverrides(
  opts: PublishOptions,
  manifest: PluginManifest | undefined,
): void {
  if (opts.artifactId && manifest?.id && opts.artifactId !== manifest.id) {
    throw new AspError(
      `artifact_id mismatch: --artifact-id=${opts.artifactId}, plugin.json id=${manifest.id}`,
      {
        code: "ARTIFACT_ID_MISMATCH",
        hint: "Remove --artifact-id to let the server parse from package, or fix the value.",
      },
    );
  }
  if (opts.version && manifest?.version && opts.version !== manifest.version) {
    throw new AspError(
      `version mismatch: --version=${opts.version}, plugin.json version=${manifest.version}`,
      {
        code: "VERSION_MISMATCH",
        hint: "Remove --version to let the server parse from package, or fix the value.",
      },
    );
  }
}

async function resolvePublishCategoryWithAutoDetect(
  opts: PublishOptions,
  zipPath: string | undefined,
  jsonOutput: boolean,
  manifest?: PluginManifest,
): Promise<PluginCategory> {
  const fromArg = parseCategory(opts.category);
  if (fromArg) return fromArg;
  if (opts.category && !fromArg) {
    throw new AspError(`Invalid --category: ${opts.category}`, {
      code: "INVALID_REQUEST",
      hint: `Use one of: ${PLUGIN_CATEGORY_OPTIONS.map((item) => item.value).join(", ")}`,
    });
  }

  const fromZip = await tryParseCategoryFromZip(zipPath, manifest);
  if (fromZip) {
    if (!jsonOutput) printInfo(`Detected category from plugin package: ${fromZip}`);
    return fromZip;
  }

  const fromUrl = tryParseCategoryFromUrl(opts.url);
  if (fromUrl) {
    if (!jsonOutput) printInfo(`Detected category from source URL: ${fromUrl}`);
    return fromUrl;
  }

  return resolvePublishCategory(opts, jsonOutput);
}

async function resolvePublishCategory(
  opts: PublishOptions,
  jsonOutput: boolean,
): Promise<PluginCategory> {
  const fromArg = parseCategory(opts.category);
  if (fromArg) return fromArg;

  if (opts.category && !fromArg) {
    throw new AspError(`Invalid --category: ${opts.category}`, {
      code: "INVALID_REQUEST",
      hint: `Use one of: ${PLUGIN_CATEGORY_OPTIONS.map((item) => item.value).join(", ")}`,
    });
  }

  if (jsonOutput || !input.isTTY || !output.isTTY) {
    throw new AspError("Plugin publish requires --category.", {
      code: "INVALID_REQUEST",
      hint: `Use --category with one of: ${PLUGIN_CATEGORY_OPTIONS.map((item) => item.value).join(", ")}`,
    });
  }

  printInfo("Select plugin category:");
  for (const [index, item] of PLUGIN_CATEGORY_OPTIONS.entries()) {
    printInfo(`${index + 1}) ${item.value} - ${item.label}`);
    printInfo(`   ${item.describe}`);
  }

  const rl = createInterface({ input, output });
  try {
    while (true) {
      const answer = (await rl.question("Enter category number or value: ")).trim();
      const index = Number.parseInt(answer, 10);
      if (!Number.isNaN(index) && index >= 1 && index <= PLUGIN_CATEGORY_OPTIONS.length) {
        const selected = PLUGIN_CATEGORY_OPTIONS[index - 1].value;
        printInfo(`Selected category: ${selected}`);
        return selected;
      }
      const byValue = parseCategory(answer);
      if (byValue) {
        printInfo(`Selected category: ${byValue}`);
        return byValue;
      }
      printWarn(`Invalid choice. Use 1-${PLUGIN_CATEGORY_OPTIONS.length} or category value.`);
    }
  } finally {
    rl.close();
  }
}

function isMissingCategoryError(err: unknown): boolean {
  if (!(err instanceof AspError)) return false;
  if (err.code !== "INVALID_REQUEST") return false;
  const message = `${err.message} ${err.hint ?? ""}`.toLowerCase();
  return message.includes("category") || message.includes("技术类型");
}

async function showPublishedPluginStatus(
  client: CliApiClient,
  pluginId: string,
  jsonOutput: boolean,
): Promise<void> {
  try {
    const info = await client.getPluginInfo(pluginId);
    if (jsonOutput) {
      printJson({
        plugin_id: info.plugin_id,
        status: "published",
        terminal: true,
        latest_version: info.latest_version,
        scan_status: info.scan_status,
        public_url: info.public_url,
        install_command: info.install_command,
      });
      return;
    }

    printInfo(`Plugin: ${info.plugin_id}`);
    printInfo("Status: published");
    printInfo("Terminal: yes");
    if (info.latest_version) printInfo(`Version: ${info.latest_version}`);
    if (info.scan_status) printInfo(`Scan: ${info.scan_status}`);
    if (info.public_url) printInfo(`URL: ${info.public_url}`);
    if (info.install_command) printInfo(`Install: ${info.install_command}`);
  } catch (err) {
    if (err instanceof AspError && err.statusCode === 404) {
      throw new AspError(`No submission or published plugin found: ${pluginId}`, {
        code: "NOT_FOUND",
        hint:
          "If the plugin is still scanning, use submission_id from publish output. " +
          "Republish on this machine to cache plugin_id mapping locally.",
      });
    }
    throw err;
  }
}

async function runPublish(
  getConfig: () => ResolvedConfig,
  zipPath: string | undefined,
  opts: PublishOptions,
): Promise<void> {
  const base = getConfig();
  const { config } = await requireCredentials(base, base.platformUrl);
  const client = new CliApiClient(config);
  const useUrlMode = Boolean(opts.url);
  const manifest = useUrlMode ? undefined : await readPluginManifestFromZip(zipPath);
  printManifestPreview(manifest, base.json);
  validateExplicitManifestOverrides(opts, manifest);

  let publishOpts: PublishOptions & { category: PluginCategory };
  try {
    const category = await resolvePublishCategoryWithAutoDetect(
      opts,
      zipPath,
      base.json,
      manifest,
    );
    publishOpts = { ...opts, category };
  } catch (err) {
    if (!isMissingCategoryError(err)) throw err;
    const category = await resolvePublishCategory({ ...opts, category: undefined }, base.json);
    publishOpts = { ...opts, category };
  }
  if (useUrlMode && zipPath) {
    throw new AspError("Do not pass zip path when using --url.", {
      code: "INVALID_REQUEST",
      hint: "Use either `asp plugin publish ./plugin.zip` or `asp plugin publish --url <url>`.",
    });
  }
  if (!useUrlMode && !zipPath) {
    throw new AspError("Zip path is required unless --url is provided.", {
      code: "INVALID_REQUEST",
      hint: "Use `asp plugin publish ./plugin.zip`.",
    });
  }

  let submission: CliSubmissionResponse;
  const tryCreateSubmission = async (): Promise<CliSubmissionResponse> => {
    if (useUrlMode) {
      return client.createArtifactSubmission(buildUrlBody(publishOpts));
    }
    const fileBuffer = await readFile(zipPath as string);
    const sha512 = createHash("sha512").update(fileBuffer).digest("hex");
    let localSubmission = await client.createArtifactSubmission(
      buildZipBody(zipPath as string, fileBuffer, sha512, publishOpts),
    );
    if (!localSubmission.upload?.url) {
      throw new AspError("Missing upload URL for zip submission.", {
        code: "UPLOAD_URL_MISSING",
      });
    }
    printInfo("Uploading plugin zip to object storage...");
    await uploadZipToSignedUrl(localSubmission.upload, fileBuffer);
    localSubmission = await client.completeArtifactSubmission(localSubmission.submission_id, {
      sha512,
      size_bytes: fileBuffer.byteLength,
    });
    return localSubmission;
  };

  try {
    submission = await tryCreateSubmission();
  } catch (err) {
    if (!isMissingCategoryError(err) || base.json || !input.isTTY || !output.isTTY) {
      throw err;
    }
    printWarn("Publish failed due to missing category, please choose one and retrying...");
    const category = await resolvePublishCategory({ ...opts, category: undefined }, base.json);
    publishOpts = { ...opts, category };
    submission = await tryCreateSubmission();
  }

  const urlPluginId = useUrlMode ? tryParsePluginIdFromUrl(publishOpts.url) : undefined;
  await recordPluginPublish({
    submissionId: submission.submission_id,
    platformUrl: config.platformUrl,
    pluginId: publishOpts.artifactId ?? manifest?.id ?? urlPluginId,
    sourceUrl: useUrlMode ? publishOpts.url : undefined,
  });

  if (base.json) {
    printJson(submission);
    return;
  }

  printSuccess(`Submission created: ${submission.submission_id}`);
  printInfo(`Initial status: ${submission.status}`);
  const trackRef =
    publishOpts.artifactId ?? manifest?.id ?? urlPluginId ?? submission.submission_id;

  if (opts.noWait) {
    printInfo(`Track progress: asp plugin publish status ${trackRef}`);
    printInfo(`Show logs: asp plugin publish logs ${trackRef}`);
    return;
  }

  if (!submission.terminal) {
    printSuccess("Upload submitted successfully. Scanning continues asynchronously.");
    printInfo(`Track progress: asp plugin publish status ${trackRef} --watch`);
    printInfo(`Show logs: asp plugin publish logs ${trackRef}`);
    return;
  }

  if (submission.status === "published") {
    printSuccess(`Published successfully (${formatPlatformLabel(config.platformUrl)})`);
    if (submission.publish?.public_url) {
      printInfo(`URL: ${submission.publish.public_url}`);
    }
    return;
  }

  printWarn(`Submission ended with status: ${submission.status}`);
  if (submission.failure?.message) {
    printWarn(`Failure: ${submission.failure.message}`);
  }
  printInfo(`Inspect logs: asp plugin publish logs ${trackRef}`);
}

async function runPublishStatus(
  getConfig: () => ResolvedConfig,
  pluginRef: string,
  opts: PublishStatusOptions,
): Promise<void> {
  const base = getConfig();
  const { config } = await requireCredentials(base, base.platformUrl);
  const client = new CliApiClient(config);

  if (looksLikeSubmissionId(pluginRef)) {
    const state = opts.watch
      ? await pollUntilTerminal(client, pluginRef, true)
      : await client.getArtifactSubmission(pluginRef);
    if (base.json) {
      printJson(state);
      return;
    }
    printInfo(`Submission: ${state.submission_id}`);
    printInfo(`Status: ${state.status}`);
    printInfo(`Terminal: ${state.terminal ? "yes" : "no"}`);
    if (state.scan?.status) printInfo(`Scan: ${state.scan.status}`);
    if (state.publish?.public_url) printInfo(`URL: ${state.publish.public_url}`);
    if (state.failure?.message) printWarn(`Failure: ${state.failure.message}`);
    return;
  }

  const submissionId = await resolvePluginSubmissionId(client, pluginRef, config.platformUrl);
  if (!submissionId) {
    await showPublishedPluginStatus(client, pluginRef, base.json);
    return;
  }

  if (!base.json) {
    printInfo(`Resolved plugin id "${pluginRef}" -> submission ${submissionId}`);
  }

  const current = await client.getArtifactSubmission(submissionId);
  if (current.terminal && current.status === "published") {
    await showPublishedPluginStatus(client, pluginRef, base.json);
    return;
  }

  const state = opts.watch
    ? await pollUntilTerminal(client, submissionId, true)
    : current;

  if (base.json) {
    printJson(state);
    return;
  }

  printInfo(`Submission: ${state.submission_id}`);
  printInfo(`Status: ${state.status}`);
  printInfo(`Terminal: ${state.terminal ? "yes" : "no"}`);
  if (state.scan?.status) printInfo(`Scan: ${state.scan.status}`);
  if (state.publish?.public_url) printInfo(`URL: ${state.publish.public_url}`);
  if (state.failure?.message) printWarn(`Failure: ${state.failure.message}`);
}

async function runPublishLogs(
  getConfig: () => ResolvedConfig,
  pluginRef: string,
  _opts: PublishLogsOptions,
): Promise<void> {
  const base = getConfig();
  const { config } = await requireCredentials(base, base.platformUrl);
  const client = new CliApiClient(config);
  const submissionId = await resolvePluginSubmissionId(client, pluginRef, config.platformUrl);
  if (!submissionId) {
    throw new AspError(`No publish logs found for plugin id: ${pluginRef}`, {
      code: "NOT_FOUND",
      hint:
        "Use submission_id from publish output, or republish on this machine to cache the mapping.",
    });
  }
  if (submissionId !== pluginRef && !base.json) {
    printInfo(`Resolved plugin id "${pluginRef}" -> submission ${submissionId}`);
  }
  const logs = await client.getArtifactSubmissionLogs(submissionId);

  if (base.json) {
    printJson(logs);
    return;
  }

  if (logs.items.length === 0) {
    printInfo("No logs yet.");
    return;
  }
  const rows = logs.items.map((item) => [item.timestamp, item.level, item.message]);
  printTable(["timestamp", "level", "message"], rows);
}

export function registerPluginCommands(program: Command, getConfig: () => ResolvedConfig): void {
  const plugin = program.command("plugin").description("Plugin marketplace and publish commands");

  plugin
    .command("search [query]")
    .description("Search plugins")
    .option("--category <code>", "Tech type/category code")
    .option("--source <source>", "official | verified | community")
    .option("--sort <sort>", "recommended | downloads | updated_at")
    .option("--page-size <n>", "Page size", (v) => Number.parseInt(v, 10))
    .option("--cursor <cursor>", "Cursor for next page")
    .action(async (query: string | undefined, opts: SearchOptions) => {
      await runSearch(getConfig, query, opts);
    });

  plugin
    .command("info <pluginId>")
    .description("Show plugin details")
    .option("--version <version>", "Specify version")
    .action(async (pluginId: string, opts: InfoOptions) => {
      await runInfo(getConfig, pluginId, opts);
    });

  plugin
    .command("versions <pluginId>")
    .description("List published versions")
    .action(async (pluginId: string) => {
      await runVersions(getConfig, pluginId);
    });

  plugin
    .command("install <pluginId>")
    .description("Install plugin via qwenpaw")
    .option("--version <version>", "Specify version")
    .option("--via <tool>", "Install tool (qwenpaw)", "qwenpaw")
    .action(async (pluginId: string, opts: InstallOptions) => {
      await runInstall(getConfig, pluginId, opts);
    });

  const publish = plugin
    .command("publish [zipPath]")
    .description("Publish plugin from local zip or URL")
    .option("--url <url>", "Publish from remote zip URL")
    .option("--artifact-id <id>", "Optional plugin id override (must match plugin.json in zip mode)")
    .option("--version <version>", "Optional version override")
    .option("--category <code>", "Plugin category code (tool|provider|command|hook|frontend|general)")
    .option("--repo-url <url>", "Repository URL")
    .option("--publish-mode <mode>", "Publish mode", "auto_after_scan")
    .option("--no-wait", "Do not wait for terminal status")
    .action(async (zipPath: string | undefined, opts: PublishOptions) => {
      await runPublish(getConfig, zipPath, opts);
    });

  publish
    .command("status <pluginId>")
    .description("Show publish status by plugin id or submission id")
    .option("--watch", "Poll until terminal status", false)
    .action(async (pluginId: string, opts: PublishStatusOptions) => {
      await runPublishStatus(getConfig, pluginId, opts);
    });

  publish
    .command("logs <pluginId>")
    .description("Show publish logs by plugin id or submission id")
    .action(async (pluginId: string, opts: PublishLogsOptions) => {
      await runPublishLogs(getConfig, pluginId, opts);
    });
}

